import Layout from "src/components/core/Layout";
import { useAuth } from "src/providers/AuthProvider";
import { AdhocKycInvalidReason } from "src/store/features/api/adhocKyc";
import { AdhocKycCard, AdhocKycPublicShell } from "src/components/adhocKyc/AdhocKycShell";
import AdhocKycStepRouter from "src/components/adhocKyc/AdhocKycStepRouter";
import InvalidLinkPage from "src/components/adhocKyc/InvalidLinkPage";
import { AdhocKycEntryKind, useAdhocKycToken } from "src/components/adhocKyc/useAdhocKycToken";

/** Bounce fragments that land straight on the terminal page, mapped to its copy. */
const FRAGMENT_REASON: Partial<Record<AdhocKycEntryKind, AdhocKycInvalidReason>> = {
  expired: "expired",
  done: "consumed",
  invalid: "invalid",
  revoked: "revoked",
  disabled: "disabled",
};

const noop = (): void => undefined;

/**
 * Adhoc Re-KYC (AU/NZ) — `/adhocKyc`. PUBLIC route: customers arrive from an
 * emailed magic link with no portal session, so this must render without one
 * (a non-public route would bounce them to login).
 *
 * Two modes, chosen by the URL fragment the backend bounce wrote:
 *  - token mode (`#akt=<token>`, or a token kept in sessionStorage across a
 *    refresh) — slim public chrome, every call to `/adhoc-kyc/t/*` with the
 *    token header; no `Layout`, whose session hooks would 401 → logout.
 *  - session mode (no token, logged in) — inside `Layout`, calls go to
 *    `/adhoc-kyc/me/*` on the normal session.
 * No token and not logged in renders the invalid page, never the login page.
 */
export default function AdhocKyc(): JSX.Element {
  const { isAuthenticated } = useAuth();
  const { entry, forgetToken } = useAdhocKycToken();
  const homePath = isAuthenticated ? "/portfolio" : "/";

  if (entry.kind === "token") {
    return (
      <AdhocKycPublicShell>
        <AdhocKycStepRouter
          auth={{ token: entry.token }}
          homePath={homePath}
          onInvalid={forgetToken}
        />
      </AdhocKycPublicShell>
    );
  }

  if (entry.kind === "none") {
    if (isAuthenticated) {
      return (
        <Layout>
          <AdhocKycCard>
            <AdhocKycStepRouter auth={{}} homePath={homePath} onInvalid={noop} />
          </AdhocKycCard>
        </Layout>
      );
    }
    return (
      <AdhocKycPublicShell>
        <InvalidLinkPage reason="invalid" homePath={homePath} />
      </AdhocKycPublicShell>
    );
  }

  return (
    <AdhocKycPublicShell>
      <InvalidLinkPage
        reason={FRAGMENT_REASON[entry.kind] as AdhocKycInvalidReason}
        token={entry.token}
        homePath={homePath}
      />
    </AdhocKycPublicShell>
  );
}
